import {
  ApiaUtil,
  ApiaUtilModalHandler,
  SimpleButton,
  TOpenModal,
} from '@apia/components';
import { TResponsiveTableRowsSelectionEvent } from '@apia/table';
import { EventEmitter, getLabel, UnSubscriber } from '@apia/util';
import { TableControllerEvents } from './table/types';
import { AdditionalFiltersModal } from './table/AdditionalFilters/AdditionalFiltersModal';
import { QueryController } from './QueryController';
import { observer } from 'mobx-react-lite';
import { Box, Spinner, ThemeUICSSObject } from '@apia/theme';

type QueryModalControllerEvents = Pick<
  TableControllerEvents,
  'onSelectRows'
> & { cancel: null };

const NavBarRenderer = observer(
  ({
    queryController,
    additionalFiltersController,
  }: {
    queryController: QueryController;
    additionalFiltersController: AdditionalFiltersModal;
  }) => (
    <>
      <SimpleButton
        variant="primary-sm"
        onClick={() => {
          queryController.refresh();
        }}
        data-test-id="btn_search"
      >
        {queryController.tableController.state.isLoading ? (
          <Spinner sx={{ color: 'white', height: '17px' }} />
        ) : (
          getLabel('btnSearch').text
        )}
      </SimpleButton>

      <SimpleButton
        size="sm"
        variant="outline"
        onClick={async () => {
          additionalFiltersController.openModal();
        }}
        data-test-id="btn-additional-filters"
      >
        {getLabel('titAdmAdtFilter').text}
      </SimpleButton>
      <SimpleButton
        size="sm"
        variant="outline"
        onClick={() => queryController.clearFilters()}
        data-test-id="btn-clear-filters"
      >
        {getLabel('btnClearFilter').text}
      </SimpleButton>
    </>
  ),
);

export class QueryModalController {
  protected emitter = new EventEmitter<QueryModalControllerEvents>();
  protected queryController: QueryController;
  private modalHandler: ApiaUtilModalHandler | null = null;
  protected currentSelection: TResponsiveTableRowsSelectionEvent | null = null;
  private additionalFiltersController: AdditionalFiltersModal;
  protected sx: ThemeUICSSObject = {};

  public constructor(queryController: QueryController) {
    if (!queryController) {
      throw new Error('QueryController instance is required');
    }
    this.queryController = queryController;
    this.additionalFiltersController = new AdditionalFiltersModal(
      this.queryController,
    );

    this.queryController.tableController.state.props = {
      isMultiple: false,
    };

    this.queryController.tableController.on('onSelectRows', (ev) => {
      this.emitter.emit('onSelectRows', ev);
      this.modalHandler?.close();
    });
    this.queryController.tableController.on('onChangeSelection', (ev) => {
      this.currentSelection = ev;
    });
  }

  protected getModalDefinition(): TOpenModal {
    const tableController = this.queryController.tableController;
    const structure = this.queryController.structure;

    return {
      children: (
        <Box sx={{ height: '100%', ...this.sx }}>
          <tableController.Context>
            <tableController.Table />
            {!structure?.queryData?.hideCount && <tableController.Pagination />}
          </tableController.Context>
        </Box>
      ),
      title: structure?.title,
      onConfirm: () => {
        this.onConfirm();
        this.additionalFiltersController.closeModal();
      },
      onCancel: () => {
        this.additionalFiltersController.closeModal();
        this.queryController.tableController.state.finishedFirstLoad = false;
        this.emitter.emit('cancel', null);
      },
      onClose: () => {
        this.additionalFiltersController.closeModal();
      },
      confirmProps: {
        confirmButtonText: getLabel('btnCon').text,
        cancelButtonText: getLabel('btnCan').text,
      },
      noFocus: true,
      NavBar: (
        <NavBarRenderer
          queryController={this.queryController}
          additionalFiltersController={this.additionalFiltersController}
        />
      ),
      size: 'finder',
    };
  }

  public getQueryController() {
    return this.queryController;
  }

  protected on<K extends keyof QueryModalControllerEvents>(
    ev: K,
    cb: (e: QueryModalControllerEvents[K]) => unknown,
  ) {
    return this.emitter.on(ev, cb);
  }

  public async openModal<T = QueryModalControllerEvents['onSelectRows']>(
    properties?: TOpenModal,
  ): Promise<T> {
    await this.queryController.initialLoad();

    if (
      this.queryController.structure?.openFiltersAutomatically ||
      this.queryController.hasRequiredColumnFilters() ||
      this.queryController.hasColumnFiltersWithDefaultValue()
    ) {
      const currentProps = this.queryController.tableController.state.props;
      this.queryController.tableController.updateState({
        props: { ...currentProps, showFilters: true },
      });
    }

    this.modalHandler = ApiaUtil.instance.modals.open({
      ...this.getModalDefinition(),
      ...properties,
    });

    return this.onConfirmResolve<T>();
  }

  public getModalHandler() {
    return this.modalHandler;
  }

  protected onConfirm() {
    if (this.queryController.tableController.state.rows.length === 1) {
      this.emitter.emit('onSelectRows', [
        {
          row: this.queryController.tableController.state.rows[0],
          index: 0,
        },
      ]);
    } else {
      this.emitter.emit('onSelectRows', this.currentSelection || []);
    }
  }

  protected onConfirmResolve<T>(): Promise<T> {
    return new Promise((resolve) => {
      let uns1: UnSubscriber = () => {},
        uns2: UnSubscriber = () => {};

      uns1 = this.on('onSelectRows', (res) => {
        uns1();
        uns2();
        resolve(res as T);
      });
      uns2 = this.on('cancel', () => {
        uns1();
        uns2();
        resolve([] as T);
      });
    });
  }
}
